import React from 'react';
import styles from './MyAccount.module.css';
import { useUserSubscription, useCancelSubscription } from '../../hooks/useSubscription';
import { Crown, Calendar, AlertCircle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SubscriptionInfo = () => {
  const { subscriptionData, isLoading, error } = useUserSubscription();
  const { cancelSubscription, isLoading: isCancelling } = useCancelSubscription();
  const navigate = useNavigate();

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to get status color and icon
  const getStatusInfo = (subscription) => {
    if (!subscription) return { color: 'gray', icon: AlertCircle, text: 'No Subscription' };
    
    if (subscription.isActive) {
      return { color: 'green', icon: CheckCircle, text: 'Active' };
    } else if (subscription.isCancelled) {
      return { color: 'red', icon: XCircle, text: 'Cancelled' };
    } else if (subscription.isExpired) {
      return { color: 'orange', icon: AlertCircle, text: 'Expired' };
    }
    
    return { color: 'gray', icon: AlertCircle, text: 'Unknown' };
  };

  if (isLoading) {
    return (
      <div className={styles.userInfo}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.userInfo}>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <p>Failed to load subscription details. Please try again later.</p>
        </div>
      </div>
    );
  }

  const subscription = subscriptionData?.subscription;
  const hasSubscription = subscriptionData?.hasSubscription;
  const statusInfo = getStatusInfo(subscription);

  return (
    <div className={styles.userInfo}>
      <div className={styles.profileHeader}>
        <div className={styles.avatarSection}>
          <div className={styles.avatar}>
            <Crown size={40} />
          </div>
          <div className={styles.userStatus}>
            <span className={`${styles.statusBadge} ${styles[`status${statusInfo.color}`]}`}>
              <statusInfo.icon size={16} />
              {statusInfo.text}
            </span>
          </div>
        </div>
      </div>

      {!hasSubscription ? (
        <div className={styles.infoGrid}>
          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>
              <Crown size={20} />
            </div>
            <div className={styles.infoContent}>
              <label>Subscription Status</label>
              <p>No Active Subscription</p>
            </div>
          </div>
          
          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>
              <AlertCircle size={20} />
            </div>
            <div className={styles.infoContent}>
              <label>Access Level</label>
              <p>Free User</p>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.infoGrid}>
          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>
              <Crown size={20} />
            </div>
            <div className={styles.infoContent}>
              <label>Plan Name</label>
              <p>{subscription?.plan_name || 'N/A'}</p>
            </div>
          </div>

          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>
              <Calendar size={20} />
            </div>
            <div className={styles.infoContent}>
              <label>Billing Cycle</label>
              <p>{subscription?.billing_cycle || 'N/A'}</p>
            </div>
          </div>

          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>
              <Calendar size={20} />
            </div>
            <div className={styles.infoContent}>
              <label>Start Date</label>
              <p>{formatDate(subscription?.start_date)}</p>
            </div>
          </div>

          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>
              <Calendar size={20} />
            </div>
            <div className={styles.infoContent}>
              <label>End Date</label>
              <p>{formatDate(subscription?.end_date)}</p>
            </div>
          </div>

          {subscription?.isActive && subscription?.daysRemaining > 0 && (
            <div className={styles.infoCard}>
              <div className={styles.infoIcon}>
                <AlertCircle size={20} />
              </div>
              <div className={styles.infoContent}>
                <label>Days Remaining</label>
                <p>{subscription.daysRemaining} days</p>
              </div>
            </div>
          )}

          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>
              <Crown size={20} />
            </div>
            <div className={styles.infoContent}>
              <label>Price</label>
              <p>₹{subscription?.price || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}

      <div className={styles.buttonGroup}>
        {hasSubscription && subscription?.isActive && (
          <button 
            className={styles.cancelSubscriptionBtn}
            onClick={() => {
              if (window.confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) {
                cancelSubscription();
              }
            }}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <span className={styles.loadingWrapper}>
                <Loader2 className={styles.spinner} size={20} />
                Cancelling...
              </span>
            ) : (
              'Cancel Subscription'
            )}
          </button>
        )}
        
        <button 
          className={styles.upgradeBtn}
          onClick={() => navigate('/subscription-plans')}
        >
          {hasSubscription ? 'Upgrade Plan' : 'Get Subscription'}
        </button>
      </div>
    </div>
  );
};

export default SubscriptionInfo; 